import { Clock, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface RecentTrace {
    id: string
    timestamp: number
    status?: 'success' | 'error' | 'unknown'
}

interface RecentTracesProps {
    onSelect: (id: string) => void
    currentTraceId?: string
}

export function RecentTraces({ onSelect, currentTraceId }: RecentTracesProps) {
    const [history, setHistory] = useState<RecentTrace[]>([])

    // Load history on mount
    useEffect(() => {
        const saved = localStorage.getItem('rag_trace_history')
        if (saved) {
            try {
                setHistory(JSON.parse(saved))
            } catch (e) {
                console.error("Failed to parse trace history", e)
            }
        }
    }, [])

    // Expose a helper to add to history (could be moved to context/hook later)
    // For MVP, we'll watch the currentTraceId prop change in the parent, 
    // but actually the parent should manage the "add to history" logic. 
    // This component purely Displays. 
    // However, to make it self-contained for "Sprint 3.1", we'll listen to storage events or provide a static list for now
    // Wait, the requirement says "Local History". Ideally parent updates localStorage, this component reads it.
    // Let's implement a "refresh" trigger or just poll/listen.

    // Better: receive history as prop? No, keep it encapsulated.
    // We'll export a helper function or assume parent handles the storage update and forces re-render if needed?
    // Actually, let's use a custom event or just reload on mount.
    // Simplified: This component manages its own state, and exposes an `addToHistory` static-like method? No.
    // Let's just make it read from props if possible, BUT for now, it reads from localStorage on mount.
    // To update, we needs a trigger.

    const removeTrace = (e: React.MouseEvent, id: string) => {
        e.stopPropagation()
        const newHistory = history.filter(h => h.id !== id)
        setHistory(newHistory)
        localStorage.setItem('rag_trace_history', JSON.stringify(newHistory))
    }

    if (history.length === 0) return null

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full overflow-hidden flex flex-col">
            <h3 className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Traces
            </h3>

            <div className="overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {history.map((trace) => (
                    <div
                        key={trace.id}
                        onClick={() => onSelect(trace.id)}
                        className={`group p-3 rounded-lg border cursor-pointer transition-all ${currentTraceId === trace.id
                            ? 'bg-blue-600/20 border-blue-500/50'
                            : 'bg-black/30 border-white/5 hover:bg-white/5 hover:border-white/10'
                            }`}
                    >
                        <div className="flex justify-between items-start mb-1">
                            <span className="font-mono text-xs text-blue-300 truncate w-32" title={trace.id}>
                                {trace.id.slice(0, 8)}...{trace.id.slice(-4)}
                            </span>
                            <button
                                onClick={(e) => removeTrace(e, trace.id)}
                                className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                        <div className="flex justify-between items-center text-[10px] text-gray-500">
                            <span>{new Date(trace.timestamp).toLocaleTimeString()}</span>
                            {trace.status && (
                                <span className={`w-2 h-2 rounded-full ${trace.status === 'success' ? 'bg-green-500' :
                                    trace.status === 'error' ? 'bg-red-500' : 'bg-gray-500'
                                    }`} />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// Helper to push to history from anywhere
export const addToTraceHistory = (id: string, status: 'success' | 'error' | 'unknown' = 'unknown') => {
    try {
        const saved = localStorage.getItem('rag_trace_history')
        let history: RecentTrace[] = saved ? JSON.parse(saved) : []

        // Remove duplicate if exists
        history = history.filter(h => h.id !== id)

        // Add to top
        history.unshift({
            id,
            timestamp: Date.now(),
            status
        })

        // Limit to 20
        history = history.slice(0, 20)

        localStorage.setItem('rag_trace_history', JSON.stringify(history))
        // Dispatch event for reactive updates if needed (not implemented yet)
    } catch (e) {
        console.error("Failed to save trace history", e)
    }
}
